import React, { useEffect, useState, useCallback } from 'react';
import { Bell, CheckCircle, XCircle, Clock, Check } from 'lucide-react';
import './styles/customer-notifications.css';

const formatStamp = (value) => {
  if (!value) return '';
  const d = new Date(value);
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  return `${date} · ${time}`;
};

const idOf = (n) => n._id || n.id;

export default function CustomerNotifications() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [marking, setMarking] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      setStatus('loading');
      const res = await fetch('/api/customer/notifications', {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error('Failed to fetch notifications');
      const data = await res.json();
      setItems(data.notifications || []);
      setStatus('succeeded');
    } catch (e) {
      setError(e.message);
      setStatus('failed');
    }
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const markRead = async (id) => {
    try {
      const res = await fetch(`/api/customer/notifications/${id}/read`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        setItems(prev => prev.map(n => idOf(n) === id ? { ...n, isRead: true } : n));
      }
    } catch (e) {
      /* non-blocking */
    }
  };

  const markAllRead = async () => {
    try {
      setMarking(true);
      const res = await fetch('/api/customer/notifications/mark-all-read', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        setItems(prev => prev.map(n => ({ ...n, isRead: true })));
      }
    } catch (e) {
      /* non-blocking */
    } finally {
      setMarking(false);
    }
  };

  const unread = items.filter(n => !n.isRead).length;

  if (status === 'loading') {
    return (
      <div className="cnotif">
        <div className="cnotif__skel cnotif__skel--head" />
        {[0, 1, 2].map(i => (
          <div key={i} className="cnotif__skel cnotif__skel--row" />
        ))}
      </div>
    );
  }

  return (
    <div className="cnotif">
      <header className="cnotif__head">
        <div className="cnotif__head-text">
          <h1 className="cnotif__title">Notifications</h1>
          <p className="cnotif__sub">
            {unread > 0 ? `${unread} unread` : "You're all caught up."}
          </p>
        </div>
        {unread > 0 && (
          <button
            type="button"
            onClick={markAllRead}
            disabled={marking}
            className="cnotif__btn cnotif__btn--ghost"
          >
            <Check size={14} aria-hidden />
            {marking ? 'Marking…' : 'Mark all read'}
          </button>
        )}
      </header>

      {error && (
        <div className="cnotif__alert cnotif__alert--error" role="alert">
          <XCircle size={14} aria-hidden /> <span>{error}</span>
        </div>
      )}

      {items.length === 0 && status === 'succeeded' ? (
        <div className="cnotif__empty">
          <Bell size={32} aria-hidden />
          <div>
            <div className="cnotif__empty-title">No notifications yet</div>
            <div className="cnotif__empty-sub">We'll ping you when something new arrives.</div>
          </div>
        </div>
      ) : (
        <ul className="cnotif__list">
          {items.map((n) => {
            const id = idOf(n);
            return (
              <li
                key={id}
                className={`cnotif-row ${n.isRead ? 'is-read' : 'is-unread'}`}
              >
                <span className="cnotif-row__indicator" aria-hidden>
                  {n.isRead ? <CheckCircle size={14} /> : <span className="cnotif-row__dot" />}
                </span>
                <div className="cnotif-row__body">
                  <div className="cnotif-row__title">{n.title}</div>
                  {n.message && <p className="cnotif-row__msg">{n.message}</p>}
                  <div className="cnotif-row__foot">
                    <span className="cnotif-row__time">
                      <Clock size={12} aria-hidden /> {formatStamp(n.createdAt)}
                    </span>
                    {!n.isRead && (
                      <button
                        type="button"
                        onClick={() => markRead(id)}
                        className="cnotif-row__mark"
                      >
                        <Check size={12} aria-hidden /> Mark as read
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
